import sanitizeSentence from '@lib/utilities/text/sanitizeSentence'
import type { TrialAssociation } from '@modules/trials/types/TrialAssociation'
import type { Outcome } from '@prisma/client'
import { parseDate } from 'chrono-node'

export type OutcomeNarrow = Pick<
  Outcome,
  | 'description'
  | 'id'
  | 'measure'
  | 'meshId'
  | 'preferredTerm'
  | 'timeframe'
  | 'type'
>

const sanitizeMeasure = (measure: string) => {
  let result = measure
  const textToStrip = [
    'Primary Outcome-',
    'Secondary Outcome-',
    'Tertiary Outcome-',
    'Primary Outcomes-',
    'Secondary Outcomes-',
    'Tertiary Outcomes-',
    'Primary Outcomes',
    'Secondary Outcomes',
    'Tertiary Outcomes',
    'Primary Outcome',
    'Secondary Outcome',
    'Tertiary Outcome',
  ]
  textToStrip.forEach((text) => {
    result = result.replace(new RegExp(text, 'ig'), '')
  })

  return result.trim()
}

const pullNumberOutOfText = (text: string) => {
  return parseInt(text.match(/\d/g)?.join('') ?? '')
}

const containsYear = (outcome: OutcomeNarrow) => {
  return outcome.timeframe.toLocaleLowerCase().includes('year')
}
const containsMonth = (outcome: OutcomeNarrow) => {
  return outcome.timeframe.toLocaleLowerCase().includes('month')
}
const containsDays = (outcome: OutcomeNarrow) => {
  return outcome.timeframe.toLocaleLowerCase().includes('day')
}
const inferTimeUnit = (outcome: OutcomeNarrow) => {
  if (containsDays(outcome)) {
    return 1
  }

  if (containsMonth(outcome)) {
    return 2
  }

  if (containsYear(outcome)) {
    return 3
  }

  return 1
}

export const compareOutcomeTimeframes = (
  a: OutcomeNarrow,
  b: OutcomeNarrow,
) => {
  const parsedA = parseDate(a.timeframe)?.getTime()
  const parsedB = parseDate(b.timeframe)?.getTime()

  // if chrono can compare them, take the chrono interpretation
  if (parsedA && parsedB) {
    return parsedB - parsedA
  }

  // Comparator returns -1 if b is greater than a
  const compareTimeUnits = inferTimeUnit(a) - inferTimeUnit(b)
  if (compareTimeUnits !== 0) {
    return compareTimeUnits
  }

  // Otherwise naively pull the integers out of the timeframes
  const difference =
    pullNumberOutOfText(a.timeframe) - pullNumberOutOfText(b.timeframe)

  if (difference === 0) {
    return b.timeframe.length - a.timeframe.length
  }
  return difference
}

const sanitizeOutcomeText = (outcome: OutcomeNarrow) => {
  // strip out the information about whether or not this is a primary outcome
  const measure = sanitizeSentence(sanitizeMeasure(outcome.measure))
  return { ...outcome, measure }
}

export const parseMeasurementsFromTrial = (
  trial: TrialAssociation,
): OutcomeNarrow[] => {
  const measurementsToShow = trial.outcomes
    .sort(compareOutcomeTimeframes)
    .map(sanitizeOutcomeText)

  return measurementsToShow
}
